// src/components/LoginPortalModal.tsx
// DROP 25 — Unified Professional Login Modal
// Server API auth · Zero credentials · Full-page overlay
// Replaces LoginPortalModal + LoginScreen with one component

import React, { useState, useEffect, useCallback } from 'react';
import { UserAccount } from '../types';
import {
  Crown, Eye, EyeOff, Lock, Mail, Loader2, X, Shield
} from 'lucide-react';

interface Props {
  isOpen:          boolean;
  currentUser:     UserAccount | null;
  onLoginSuccess:  (user: UserAccount) => void;
  onClose?:        () => void;
  canDismiss?:     boolean;
}

const LOGO_URL     = "https://res.cloudinary.com/uuovmrek/image/upload/e_bgremoval/uzairuelogo01";
const LOGO_FB      = "https://res.cloudinary.com/uuovmrek/image/upload/uzairuelogo01";
const OGIENENI_URL = "https://res.cloudinary.com/uuovmrek/image/upload/ogienenimai002";
const PALACE_URL   = "https://res.cloudinary.com/uuovmrek/image/upload/uzairuepalace001";

const C = {
  bg:       "#070D0A",
  surface:  "#0E1812",
  card:     "#121E16",
  border:   "#1C2E22",
  gold:     "#C9A84C",
  goldLt:   "#E2C97E",
  text:     "#EDE8DC",
  muted:    "rgba(237,232,220,0.5)",
  dim:      "rgba(237,232,220,0.25)",
  green:    "#0F5132",
  darkGreen:"#0A3D26",
  red:      "#ef4444",
  redBg:    "rgba(239,68,68,0.1)",
  redBorder:"rgba(239,68,68,0.3)",
};

export default function LoginPortalModal({
  isOpen,
  currentUser,
  onLoginSuccess,
  onClose,
  canDismiss = false,
}: Props) {

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [error,    setError]    = useState('');
  const [busy,     setBusy]     = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setShowPw(false);
      setError('');
      setBusy(false);
    }
  }, [isOpen]);

  // Escape key to close (if dismissible)
  useEffect(() => {
    if (!isOpen || !canDismiss) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, canDismiss, onClose]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const res = await fetch('/api/auth/login', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (data.success && data.user) {
        // Store token if returned
        if (data.accessToken || data.token) {
          localStorage.setItem('erue_auth_token', data.accessToken || data.token);
          if (data.refreshToken) { localStorage.setItem('erue_refresh_token', data.refreshToken); }
        }
        localStorage.setItem('erue_auth_data', JSON.stringify(data.user));
        onLoginSuccess(data.user as UserAccount);
      } else {
        setError(data.error || data.message || 'Invalid credentials. Please try again.');
      }
    } catch {
      setError('Connection error — please check your network and try again.');
    } finally {
      setBusy(false);
    }
  }, [email, password, onLoginSuccess]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position:   'fixed',
        inset:      0,
        zIndex:     2000,
        display:    'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(4,8,6,0.92)',
        backdropFilter: 'blur(8px)',
        padding:    16,
        overflowY:  'auto',
      }}
      onClick={canDismiss && onClose ? (e) => {
        if (e.target === e.currentTarget) onClose();
      } : undefined}
    >
      <div
        style={{
          display:      'flex',
          width:        '100%',
          maxWidth:     880,
          minHeight:    520,
          borderRadius: 18,
          overflow:     'hidden',
          boxShadow:    '0 32px 80px rgba(0,0,0,0.8)',
          border:       `1px solid ${C.border}`,
          position:     'relative',
          margin:       'auto',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Staff Login"
      >

        {/* ── LEFT PANEL — Kingdom Identity (desktop only) ────────── */}
        <div
          className="hidden lg:flex"
          style={{
            width:          '45%',
            flexShrink:     0,
            flexDirection:  'column',
            justifyContent: 'space-between',
            position:       'relative',
            overflow:       'hidden',
            background:     C.darkGreen,
          }}
        >
          {/* Palace background */}
          <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
            <img
              src={PALACE_URL}
              alt="Palace of the Ogieneni"
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center',
                filter: 'brightness(0.22) saturate(1.2)',
              }}
            />
            {/* Green gradient overlay */}
            <div style={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(160deg, ${C.darkGreen}cc 0%, rgba(15,81,50,0.55) 50%, ${C.darkGreen}ee 100%)`,
            }} />
          </div>

          {/* Top — Logo + title */}
          <div style={{ position: 'relative', zIndex: 1, padding: '32px 28px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: C.bg,
                border: `1px solid ${C.border}`,
                overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <img
                  src={LOGO_URL}
                  alt="Uzairue Logo"
                  style={{ width: 40, height: 40, objectFit: 'contain', mixBlendMode: 'screen' }}
                  onError={e => { (e.target as HTMLImageElement).src = LOGO_FB; }}
                />
              </div>
              <div>
                <div style={{
                  fontFamily: 'Cinzel, serif', fontSize: 13, fontWeight: 700,
                  color: C.gold, letterSpacing: 0.5, lineHeight: 1.2,
                }}>ERUE Festival 2026</div>
                <div style={{ fontSize: 9, color: C.dim, letterSpacing: 2, textTransform: 'uppercase' }}>
                  Uzairue Kingdom
                </div>
              </div>
            </div>
          </div>

          {/* Centre — Ogieneni portrait + Kingdom info */}
          <div style={{
            position: 'relative', zIndex: 1,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', padding: '0 28px',
          }}>
            {/* Portrait */}
            <div style={{
              width: 120, height: 140,
              borderRadius: 14,
              overflow: 'hidden',
              border: `2px solid rgba(201,168,76,0.5)`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
              marginBottom: 16,
              background: 'rgba(15,81,50,0.4)',
            }}>
              <img
                src={OGIENENI_URL}
                alt="HRH The Ogieneni of Uzairue Kingdom"
                style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover', objectPosition: 'top',
                  display: 'block',
                }}
                onError={e => {
                  const el = e.target as HTMLImageElement;
                  el.style.display = 'none';
                  if (el.parentElement) {
                    el.parentElement.style.display = 'flex';
                    el.parentElement.style.alignItems = 'center';
                    el.parentElement.style.justifyContent = 'center';
                    el.parentElement.innerHTML =
                      `<span style="font-size:48px">👑</span>`;
                  }
                }}
              />
            </div>

            <div style={{
              fontFamily: 'Cinzel, serif',
              fontSize: 18, fontWeight: 700,
              color: C.text, textAlign: 'center', lineHeight: 1.3,
              marginBottom: 6,
            }}>
              Uzairue Kingdom
            </div>
            <div style={{ width: 40, height: 1, background: C.gold, margin: '0 auto 10px' }} />
            <div style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 12, color: C.gold,
              textAlign: 'center', fontStyle: 'italic', lineHeight: 1.5,
              marginBottom: 6,
            }}>
              "One People, One Kingdom, One Destiny"
            </div>
            <div style={{ fontSize: 10, color: C.dim, textAlign: 'center' }}>
              Etsako West LGA · Edo State · Nigeria
            </div>

            {/* Festival dates badge */}
            <div style={{
              marginTop: 20,
              padding: '10px 20px',
              borderRadius: 10,
              border: `1px solid rgba(201,168,76,0.3)`,
              background: 'rgba(201,168,76,0.07)',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 9, color: C.gold, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 3 }}>
                Festival Dates
              </div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: 16, fontWeight: 700, color: C.text }}>
                Dec 24 – 30, 2026
              </div>
              <div style={{ fontSize: 9, color: C.dim, marginTop: 2 }}>
                Grand Finale · Wednesday 30 December
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div style={{ position: 'relative', zIndex: 1, padding: '0 28px 24px', textAlign: 'center' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontSize: 9, color: C.dim, letterSpacing: 0.5,
            }}>
              <Shield size={10} style={{ color: C.gold }} />
              Under the Royal Patronage of HRH The Ogieneni
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL — Login Form ────────────────────────────── */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: C.bg,
          position: 'relative',
        }}>

          {/* Dismiss button */}
          {canDismiss && onClose && (
            <button
              onClick={onClose}
              style={{
                position: 'absolute', top: 16, right: 16,
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                color: C.muted,
                zIndex: 10,
              }}
              aria-label="Close login"
            >
              <X size={16} />
            </button>
          )}

          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '48px 40px',
          }}>

            {/* Mobile logo */}
            <div
              className="flex lg:hidden"
              style={{
                flexDirection: 'column', alignItems: 'center',
                marginBottom: 32,
              }}
            >
              <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: C.bg,
                border: `1px solid ${C.border}`,
                overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 10,
              }}>
                <img
                  src={LOGO_URL}
                  alt="Logo"
                  style={{ width: 48, height: 48, objectFit: 'contain', mixBlendMode: 'screen' }}
                  onError={e => { (e.target as HTMLImageElement).src = LOGO_FB; }}
                />
              </div>
              <div style={{ fontFamily: 'Cinzel, serif', fontSize: 16, fontWeight: 700, color: C.gold }}>
                ERUE Festival 2026
              </div>
              <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>Uzairue Kingdom</div>
            </div>

            {/* Heading */}
            <div style={{ marginBottom: 28 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
              }}>
                <Shield size={16} style={{ color: C.gold }} />
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: 2,
                  textTransform: 'uppercase', color: C.gold,
                }}>
                  Staff Portal Access
                </span>
              </div>
              <h2 style={{
                fontFamily: 'Cinzel, serif',
                fontSize: 26, fontWeight: 700,
                color: C.text, margin: 0, lineHeight: 1.2,
              }}>
                Sign In
              </h2>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>
                Authorised personnel only
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>

              {/* Email */}
              <div style={{ marginBottom: 16 }}>
                <label style={{
                  display: 'block', fontSize: 11, fontWeight: 600,
                  letterSpacing: 1, textTransform: 'uppercase',
                  color: C.muted, marginBottom: 6,
                }}>
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail
                    size={15}
                    style={{
                      position: 'absolute', left: 14,
                      top: '50%', transform: 'translateY(-50%)',
                      color: 'rgba(201,168,76,0.6)', pointerEvents: 'none',
                    }}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    autoComplete="username"
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '12px 14px 12px 42px',
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      color: C.text,
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.15s',
                    }}
                    onFocus={e => (e.target.style.borderColor = 'rgba(201,168,76,0.5)')}
                    onBlur={e  => (e.target.style.borderColor = C.border)}
                  />
                </div>
              </div>

              {/* Password */}
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block', fontSize: 11, fontWeight: 600,
                  letterSpacing: 1, textTransform: 'uppercase',
                  color: C.muted, marginBottom: 6,
                }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock
                    size={15}
                    style={{
                      position: 'absolute', left: 14,
                      top: '50%', transform: 'translateY(-50%)',
                      color: 'rgba(201,168,76,0.6)', pointerEvents: 'none',
                    }}
                  />
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    style={{
                      width: '100%',
                      padding: '12px 44px 12px 42px',
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      color: C.text,
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.15s',
                    }}
                    onFocus={e => (e.target.style.borderColor = 'rgba(201,168,76,0.5)')}
                    onBlur={e  => (e.target.style.borderColor = C.border)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(p => !p)}
                    style={{
                      position: 'absolute', right: 14,
                      top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: C.dim, padding: 0,
                    }}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '10px 14px',
                  background: C.redBg,
                  border: `1px solid ${C.redBorder}`,
                  borderRadius: 8,
                  marginBottom: 16,
                  fontSize: 13, color: '#FCA5A5', lineHeight: 1.4,
                }}>
                  <span style={{ flexShrink: 0, marginTop: 1 }}>⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={busy}
                style={{
                  width: '100%',
                  padding: '13px 24px',
                  background: busy ? 'rgba(201,168,76,0.4)' : C.gold,
                  border: 'none',
                  borderRadius: 10,
                  color: '#040806',
                  fontSize: 14, fontWeight: 700,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'all 0.15s',
                  letterSpacing: 0.5,
                }}
                onMouseEnter={e => {
                  if (!busy) (e.currentTarget as HTMLButtonElement).style.background = C.goldLt;
                }}
                onMouseLeave={e => {
                  if (!busy) (e.currentTarget as HTMLButtonElement).style.background = C.gold;
                }}
              >
                {busy ? (
                  <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Signing in…</>
                ) : (
                  <>Sign In →</>
                )}
              </button>
            </form>

            {/* Footer note */}
            <p style={{
              textAlign: 'center', fontSize: 11,
              color: C.dim, marginTop: 24, lineHeight: 1.6,
            }}>
              Authorised Personnel Only<br />
              Account access granted by Platform Administrator.<br />
              Contact <span style={{ color: C.gold }}>erueuzairue@gmail.com</span>
            </p>
          </div>

          {/* Bottom bar */}
          <div style={{
            padding: '10px 40px',
            borderTop: `1px solid ${C.border}`,
            background: C.surface,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            fontSize: 9, color: C.dim, letterSpacing: 1,
          }}>
            <Shield size={9} style={{ color: C.gold }} />
            ERUE Festival Suite · Uzairue Kingdom Royal Secretariat
          </div>
        </div>
      </div>

      {/* Spinner keyframe */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (min-width: 1024px) {
          .hidden.lg\\:flex { display: flex !important; }
          .flex.lg\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
